import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
    LogIn,
    LayoutDashboard,
    Factory,
    Minus,
    ShieldCheck,
    Package,
} from "lucide-react";
import { toast } from "sonner";
import PublicNav from "@/components/PublicNav";

const publicApi = axios.create({ baseURL: API });

export default function PublicProduction() {
    const { token } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null); // product being withdrawn
    const [qty, setQty] = useState("1");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await publicApi.get("/public/production");
            setItems(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openDialog = (p) => {
        setSelected(p);
        setQty("1");
        setName("");
    };

    const submit = async () => {
        const q = parseInt(qty, 10);
        if (!q || q <= 0) return toast.error("Quantité invalide");
        if (q > selected.quantity) return toast.error("Stock insuffisant");
        setSubmitting(true);
        try {
            await publicApi.post("/public/production/withdraw", {
                product_id: selected.id,
                quantity: q,
                name,
            });
            toast.success(`${q} ${selected.name} retiré(s)`);
            setSelected(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen bg-[#F7F3E8]">
            <header className="sticky top-0 z-20 bg-[#2E3A22] text-[#EDE7D3] border-b border-[#3D4E2E] shadow-sm">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75C] font-semibold">
                            Raza Perdida MC
                        </div>
                        <div
                            data-testid="public-brand"
                            className="font-mc text-xl sm:text-2xl font-semibold tracking-tight mt-0.5"
                        >
                            Production
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <PublicNav />
                        {token ? (
                            <Link to="/dashboard">
                                <Button
                                    data-testid="public-open-dashboard-btn"
                                    className="bg-[#F5EFDA] hover:bg-[#EDE4C0] text-[#2E3A22]"
                                >
                                    <LayoutDashboard className="w-4 h-4 mr-2" />
                                    Tableau de bord
                                </Button>
                            </Link>
                        ) : (
                            <Link to="/login">
                                <Button
                                    data-testid="public-login-btn"
                                    className="bg-[#B8934A] hover:bg-[#A07E36] text-white"
                                >
                                    <LogIn className="w-4 h-4 mr-2" />
                                    Accès membre
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
                <div className="flex items-center gap-3 text-sm text-[#5A6F45] bg-[#EDE7D3] border border-[#DDD3B9] rounded-md px-4 py-3">
                    <ShieldCheck className="w-4 h-4 shrink-0" />
                    <span>
                        Retirez des items de la production. Chaque sortie est
                        enregistrée dans l'historique.
                    </span>
                </div>

                {loading ? (
                    <div className="text-slate-500 text-sm text-center py-10">
                        Chargement…
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-slate-500 text-sm text-center py-16 border border-dashed border-slate-200 rounded-lg bg-white">
                        Aucun produit disponible en Production.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((it) => (
                            <Card
                                key={it.id}
                                data-testid={`production-card-${it.id}`}
                                className="border-slate-200 shadow-sm p-5 bg-white flex flex-col"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <div className="w-9 h-9 rounded-md bg-[#F0E5C7] text-[#7A6018] flex items-center justify-center shrink-0">
                                                <Factory className="w-4 h-4" />
                                            </div>
                                            <div className="font-medium text-slate-900 truncate">
                                                {it.name}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                                            Disponible
                                        </div>
                                        <div
                                            className={`font-display text-2xl font-bold tabular-nums mt-0.5 ${
                                                it.quantity === 0
                                                    ? "text-rose-700"
                                                    : "text-slate-900"
                                            }`}
                                        >
                                            {it.quantity}
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    onClick={() => openDialog(it)}
                                    disabled={it.quantity === 0}
                                    data-testid={`production-withdraw-btn-${it.id}`}
                                    className="mt-5 bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
                                >
                                    <Minus className="w-4 h-4 mr-2" />
                                    Retirer
                                </Button>
                            </Card>
                        ))}
                    </div>
                )}
            </main>

            <footer className="max-w-6xl mx-auto px-6 pb-10 text-center text-xs text-slate-500">
                Raza Perdida MC - Retraits Production
            </footer>

            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent data-testid="production-withdraw-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            Retirer — {selected?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Stock disponible : {selected?.quantity}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Quantité à retirer
                            </Label>
                            <Input
                                data-testid="production-quantity-input"
                                type="number"
                                min="1"
                                max={selected?.quantity}
                                value={qty}
                                onChange={(e) => setQty(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Votre nom (optionnel)
                            </Label>
                            <Input
                                data-testid="production-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ex : Diego"
                                className="mt-2 h-11"
                            />
                            <div className="text-xs text-slate-500 mt-1">
                                Traçabilité dans l'historique. Laissé vide → "Anonyme".
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="ghost"
                            data-testid="production-cancel-btn"
                            onClick={() => setSelected(null)}
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={submitting}
                            data-testid="production-submit-btn"
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                        >
                            {submitting ? "Envoi…" : "Confirmer le retrait"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
